import { erApiFeil } from '../api/feil.ts';
import { andel, useStats, type FunnelWeek } from '../api/useStats.ts';
import { IKKE_NOK } from '../programmer/Rapport.tsx';
import { Ikon } from '../status/Ikon.tsx';

const UKER = 12;

/**
 * Bruk per produkt (M4.2, maaling.md «Bruk»): tragten i widgeten — åpnet → spor valgt →
 * sendt — innsendinger og anonyme, og SDK-versjonsspredning. Tellinger summert over de
 * siste ukene; frafallet per steg er funnet. Andeler først ved 30 i nevneren (DD-16).
 */
export function ProduktBruk({ productId }: { productId: string }) {
  const stats = useStats(UKER, productId);
  if (stats.isPending) {
    return (
      <p role="status" className="panel-tekst-liten">
        Henter bruk …
      </p>
    );
  }
  if (stats.isError) {
    return (
      <p role="alert" className="panel-tekst-liten panel-innst-feil">
        <Ikon navn="warning-outline" /> Kunne ikke hente bruk. {erApiFeil(stats.error) ? stats.error.brukertekst : ''}
      </p>
    );
  }

  const t = sum(stats.data.funnel);
  const innsendinger = stats.data.submissions.reduce((s, u) => s + u.total, 0);
  const anonyme = stats.data.submissions.reduce((s, u) => s + u.anonymous, 0);

  return (
    <div className="panel-innst-bruk" data-testid={`bruk-${productId}`}>
      <p className="panel-etikett">Bruk siste {UKER} uker — tellinger, aldri personer.</p>
      <dl className="panel-prg-tellinger">
        <Tall navn="Åpnet widgeten" verdi={t.opened} />
        <Tall navn="Valgte feil eller ønske" verdi={t.selectedFeil + t.selectedOnske} hint={`${t.selectedFeil} feil · ${t.selectedOnske} ønske`} />
        <Tall navn="Sendte" verdi={t.sentFeil + t.sentOnske} hint={andel(t.sentFeil + t.sentOnske, t.opened) === null ? IKKE_NOK : `${andel(t.sentFeil + t.sentOnske, t.opened)} av åpningene`} testId={`bruk-sendte-${productId}`} />
        <Tall navn="Feilet" verdi={t.failed} hint={t.queuedOffline > 0 ? `${t.queuedOffline} køet offline` : undefined} />
        <Tall navn="Innsendinger" verdi={innsendinger} hint={`${anonyme} anonyme`} testId={`bruk-innsendinger-${productId}`} />
        <Tall navn="Traff kjent feil" verdi={t.knownIssueConfirmed} hint={`${t.knownIssueShown} viste kort`} />
      </dl>
      {stats.data.sdkVersions.length > 0 ? (
        <p className="panel-etikett" data-testid={`bruk-sdk-${productId}`}>
          SDK-versjoner: {stats.data.sdkVersions.map((v) => `${v.sdkVersion} (${v.submissions})`).join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

/** Sløyfen ut (maaling.md, M1.4): hendelser skrevet, levert innen 5 forsøk, dead-letter — feiler denne, brytes alle over uten at noen ser det. */
export function SloeyfenUt() {
  const stats = useStats(UKER);
  if (!stats.data) return null;
  const w = stats.data.webhooks;
  return (
    <p className="panel-etikett" data-testid="sloeyfen-ut">
      <Ikon navn="swap-horizontal-outline" /> Siste {UKER} uker: {w.events} hendelser · {w.deliveredWithinFive} levert innen 5 forsøk · {w.dead} gitt opp
      {andel(w.deliveredWithinFive, w.events) ? ` · ${andel(w.deliveredWithinFive, w.events)} fram` : ''}
    </p>
  );
}

function sum(uker: FunnelWeek[]) {
  return uker.reduce(
    (s, u) => ({
      opened: s.opened + u.opened,
      selectedFeil: s.selectedFeil + u.selectedFeil,
      selectedOnske: s.selectedOnske + u.selectedOnske,
      knownIssueShown: s.knownIssueShown + u.knownIssueShown,
      knownIssueConfirmed: s.knownIssueConfirmed + u.knownIssueConfirmed,
      sentFeil: s.sentFeil + u.sentFeil,
      sentOnske: s.sentOnske + u.sentOnske,
      failed: s.failed + u.failed,
      queuedOffline: s.queuedOffline + u.queuedOffline,
    }),
    { opened: 0, selectedFeil: 0, selectedOnske: 0, knownIssueShown: 0, knownIssueConfirmed: 0, sentFeil: 0, sentOnske: 0, failed: 0, queuedOffline: 0 },
  );
}

function Tall({ navn, verdi, hint, testId }: { navn: string; verdi: number; hint?: string; testId?: string }) {
  return (
    <div className="panel-prg-telling" data-testid={testId}>
      <dt className="panel-etikett">{navn}</dt>
      <dd className="panel-prg-tall">
        {verdi}
        {hint ? <span className="panel-etikett"> — {hint}</span> : null}
      </dd>
    </div>
  );
}
